import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Form, Input as AntInput, Row, Col, Button, Select, message } from "antd";
import { useTranslation } from 'react-i18next';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import { getUsers } from '../../Users/UsersServices';
import { createTransaction } from '../TransactionsServices';
import { resolveBackendMessage } from '../../../Utils/apiMessages';
import { debounce } from 'lodash';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';

const AddTransactionModal = ({ onCancel, onTransactionAdded }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [users, setUsers] = useState([]);
    const [searchStringUser, setSearchStringUser] = useState('');
    const [searchUsersLoading, setSearchUsersLoading] = useState(false);
    const [addTransactionLoading, setAddTransactionLoading] = useState(false);
    const { handleError } = useErrorHandler();
    const usersAbortControllerRef = useRef(null);

    const fetchUsers = useCallback(async (search_string) => {
        if (usersAbortControllerRef.current) {
            usersAbortControllerRef.current.abort();
        }

        const controller = new AbortController();
        usersAbortControllerRef.current = controller;

        try {
            setSearchUsersLoading(true);
            const response = await getUsers({ page: 1, size: 100, search_string, signal: controller.signal });
            if (controller.signal.aborted) return;
            setUsers(response.data.map(user => ({
                label: user.full_name,
                value: user.id
            }))
            );
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || controller.signal.aborted) return;
            handleError(error);
        } finally {
            if (usersAbortControllerRef.current === controller && !controller.signal.aborted) {
                setSearchUsersLoading(false);
            }
        }
    }, [setUsers, handleError]);

    const debouncedFetchUsers = useCallback(
        debounce((search) => {
            fetchUsers(search);
        }, 500),
        [fetchUsers]
    );

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers, debouncedFetchUsers]);

    useEffect(() => () => {
        debouncedFetchUsers.cancel?.();
        if (usersAbortControllerRef.current) {
            usersAbortControllerRef.current.abort();
        }
    }, [debouncedFetchUsers]);

    const handleAddTransaction = async (values) => {
        try {
            setAddTransactionLoading(true);
            const payload = await createTransaction({
                ...values,
                coins_amount: Number(values.coins_amount),
                paid_amount: Number(values.paid_amount)
            });
            {
                const text = resolveBackendMessage(t, payload);
                if (text) message.success(text);
            }
            onTransactionAdded();
            onCancel();
        } catch (error) {
            handleError(error, form);
        } finally {
            setAddTransactionLoading(false);
        }
    };

    return (
        <ResponsiveModal
            title={t('add_new_transaction')}
            onCancel={onCancel}
            desktopWidth={720}
            open
            footer={
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        type="primary"
                        onClick={() => form.submit()}
                        loading={addTransactionLoading}
                    >
                        {t('add_transaction')}
                    </Button>
                </div>
            }
        >
            <Form form={form} onFinish={handleAddTransaction}>
                <Row gutter={16} style={{ width: '100%', overflow: 'hidden' }}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            style={{ marginBottom: 4 }}
                            label={t('user')}
                            name="user_id"
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 24 }}
                            rules={[{ required: true, message: t('please_select_a_user') }]}
                        >
                            <Select
                                showSearch
                                placeholder={t('select_a_user')}
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={users}
                                onSearch={(value) => {
                                    setSearchStringUser(value);
                                    debouncedFetchUsers(value);
                                }}
                                value={searchStringUser}
                                loading={searchUsersLoading}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            style={{ marginBottom: 4 }}
                            label={t('coins_amount')}
                            name="coins_amount"
                            labelCol={{ span: 24 }}
                            rules={[{ required: true, message: t('please_input_the_amount_of_coins') }]}
                        >
                            <AntInput type="number" placeholder={t('enter_coins_amount')} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            style={{ marginBottom: 4 }}
                            label={t('amount_paid')}
                            name="paid_amount"
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 24 }}
                            rules={[{ required: true, message: t('please_input_the_amount_paid') }]}
                        >
                            <AntInput type="number" placeholder={t('enter_amount_paid')} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            style={{ marginBottom: 4 }}
                            label={t('description')}
                            name="description"
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 24 }}
                            rules={[{ required: true, message: t('please_input_a_description') }]}
                        >
                            <AntInput placeholder={t('enter_transaction_description')} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </ResponsiveModal>
    );
};

export default AddTransactionModal;
